import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Region } from "../shared/protocol"
import { difference, normalize, totalMs, union } from "../shared/regions"

// The ideal-region annotation. The listener drags over a timeline of the
// session to mark where the VAD should have been producing audio; that set
// is I in model_spec.md, and both error rates are measured against it.
//
// Neither arm's output is drawn here, and the arm cards are hidden by the
// page while this is on screen. The guide is the speech probability trace,
// which is the same for both arms because the model runs once per frame —
// so it orients the listener without pulling the annotation toward whichever
// alternative they are about to be asked to choose between.

const HEIGHT = 150
const RULER_H = 18
const STRIP_H = 26
// A canvas this wide is already past the point where browsers get fussy, so
// zoom levels that would exceed it are dropped. It only bites on sessions
// of several minutes, where the coarsest zoom is still about two pixels per
// frame.
const MAX_CANVAS_PX = 20_000
const ZOOMS = [20, 50, 100, 200]
// Shorter than this and a drag is taken as a click, which seeks instead of
// painting. Roughly one frame at the default zoom.
const CLICK_MS = 40

type Mode = "paint" | "erase"

interface Drag {
  fromMs: number
  toMs: number
  mode: Mode
}

export function Annotator({
  trace,
  msPerFrame,
  durationMs,
  audio,
  regions,
  onChange,
}: {
  trace: number[]
  msPerFrame: number
  durationMs: number
  /** 16 kHz mono, for playback only — it is never uploaded. */
  audio: Float32Array
  regions: Region[]
  onChange: (regions: Region[]) => void
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const dragRef = useRef<Drag | null>(null)
  const playheadRef = useRef(0)
  const regionsRef = useRef(regions)
  regionsRef.current = regions

  const [mode, setMode] = useState<Mode>("paint")
  const [playing, setPlaying] = useState(false)

  // The widest zoom is always the one that makes the session exactly fill
  // the panel, which is why it is measured rather than picked from a list:
  // a short recording shown at a fixed scale would sit in a stub of a canvas
  // with the rest of the panel empty.
  const [fitPps, setFitPps] = useState(0)
  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return
    const measure = () =>
      setFitPps(Math.max(1, wrap.clientWidth / (durationMs / 1000)))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(wrap)
    return () => observer.disconnect()
  }, [durationMs])

  const zooms = useMemo(() => {
    const closer = ZOOMS.filter(
      (p) => p > fitPps * 1.2 && (durationMs / 1000) * p <= MAX_CANVAS_PX
    )
    return [fitPps, ...closer]
  }, [fitPps, durationMs])

  // Null until the listener picks a zoom, so that until they do, the view
  // keeps following the panel width.
  const [chosenPps, setChosenPps] = useState<number | null>(null)
  const pps = chosenPps ?? fitPps

  const totalPx = Math.max(1, Math.round((durationMs / 1000) * pps))
  const msToPx = useCallback((ms: number) => (ms / 1000) * pps, [pps])
  const pxToMs = useCallback(
    (px: number) => Math.min(durationMs, Math.max(0, (px / pps) * 1000)),
    [pps, durationMs]
  )

  // --- playback -----------------------------------------------------------
  // Built lazily so the page does not open an AudioContext it may never use.
  const audioCtxRef = useRef<AudioContext | null>(null)
  const bufferRef = useRef<AudioBuffer | null>(null)
  const sourceRef = useRef<AudioBufferSourceNode | null>(null)
  const startedAtRef = useRef(0)
  const startOffsetRef = useRef(0)

  const stopPlayback = useCallback(() => {
    const src = sourceRef.current
    sourceRef.current = null
    if (src) {
      src.onended = null
      try {
        src.stop()
      } catch {
        // Already ended; nothing to stop.
      }
    }
    setPlaying(false)
  }, [])

  const play = useCallback(
    async (fromMs: number) => {
      let ctx = audioCtxRef.current
      if (!ctx) {
        ctx = audioCtxRef.current = new AudioContext()
        // createBuffer at 16 kHz rather than the context's rate; Web Audio
        // resamples a buffer source whose rate differs from the context's.
        const buffer = ctx.createBuffer(1, Math.max(1, audio.length), 16000)
        buffer.getChannelData(0).set(audio)
        bufferRef.current = buffer
      }
      await ctx.resume()
      stopPlayback()
      const src = ctx.createBufferSource()
      src.buffer = bufferRef.current
      src.connect(ctx.destination)
      src.onended = () => {
        if (sourceRef.current === src) {
          sourceRef.current = null
          setPlaying(false)
        }
      }
      src.start(0, Math.max(0, fromMs / 1000))
      sourceRef.current = src
      startedAtRef.current = ctx.currentTime
      startOffsetRef.current = fromMs / 1000
      setPlaying(true)
    },
    [audio, stopPlayback]
  )

  const currentMs = useCallback(() => {
    const ctx = audioCtxRef.current
    if (!playing || !ctx) return playheadRef.current
    const elapsed = ctx.currentTime - startedAtRef.current + startOffsetRef.current
    return Math.min(durationMs, elapsed * 1000)
  }, [playing, durationMs])

  useEffect(() => {
    return () => {
      stopPlayback()
      void audioCtxRef.current?.close()
    }
  }, [stopPlayback])

  // --- drawing ------------------------------------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    if (canvas.width !== Math.round(totalPx * dpr)) {
      canvas.width = Math.round(totalPx * dpr)
      canvas.height = Math.round(HEIGHT * dpr)
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, totalPx, HEIGHT)

    const style = getComputedStyle(canvas)
    const accent = style.getPropertyValue("--color-success").trim() || "#34d399"
    const danger = style.getPropertyValue("--color-error").trim() || "#f87171"

    const traceTop = RULER_H
    const traceH = HEIGHT - RULER_H - STRIP_H
    const stripTop = HEIGHT - STRIP_H

    ctx.fillStyle = "#000"
    ctx.fillRect(0, 0, totalPx, HEIGHT)

    // Ruler. The step is the coarsest one that still keeps labels apart.
    const step =
      [0.5, 1, 2, 5, 10, 30, 60, 120].find((s) => s * pps >= 70) ?? 300
    ctx.fillStyle = "rgba(226,232,240,0.5)"
    ctx.font = "11px ui-monospace, monospace"
    ctx.textBaseline = "top"
    for (let t = 0; t * 1000 <= durationMs; t += step) {
      const x = msToPx(t * 1000)
      ctx.fillRect(x, RULER_H - 5, 1, 5)
      ctx.fillText(formatTime(t), x + 3, 2)
    }

    // Speech probability, filled so the shape reads at a glance.
    ctx.beginPath()
    ctx.moveTo(0, traceTop + traceH)
    for (let i = 0; i < trace.length; i++) {
      ctx.lineTo(msToPx(i * msPerFrame), traceTop + traceH - trace[i]! * traceH)
    }
    ctx.lineTo(msToPx(trace.length * msPerFrame), traceTop + traceH)
    ctx.closePath()
    ctx.fillStyle = "rgba(226,232,240,0.18)"
    ctx.fill()
    ctx.strokeStyle = "#e2e8f0"
    ctx.lineWidth = 1
    ctx.stroke()

    // Empty track beneath the trace, so an unmarked timeline still reads as
    // something to draw on.
    ctx.fillStyle = "rgba(226,232,240,0.15)"
    ctx.fillRect(0, stripTop + 6, totalPx, STRIP_H - 12)

    // Committed regions: a wash over the trace plus a solid bar in the track.
    for (const r of regionsRef.current) {
      const x = msToPx(r.startMs)
      const w = Math.max(1, msToPx(r.endMs) - x)
      ctx.fillStyle = accent
      ctx.globalAlpha = 0.22
      ctx.fillRect(x, traceTop, w, traceH)
      ctx.globalAlpha = 1
      ctx.fillRect(x, stripTop + 6, w, STRIP_H - 12)
    }

    // The drag in progress, shown before it is committed.
    const drag = dragRef.current
    if (drag) {
      const lo = Math.min(drag.fromMs, drag.toMs)
      const hi = Math.max(drag.fromMs, drag.toMs)
      const x = msToPx(lo)
      const w = Math.max(1, msToPx(hi) - x)
      ctx.globalAlpha = 0.35
      ctx.fillStyle = drag.mode === "paint" ? accent : danger
      ctx.fillRect(x, traceTop, w, traceH + STRIP_H)
      ctx.globalAlpha = 1
    }

    const head = msToPx(currentMs())
    ctx.fillStyle = "#f8fafc"
    ctx.fillRect(head, RULER_H, 1.5, HEIGHT - RULER_H)
  }, [totalPx, pps, trace, msPerFrame, durationMs, msToPx, currentMs])

  useEffect(() => {
    draw()
  }, [draw, regions])

  // Follow the playhead while it runs, scrolling only once it leaves view.
  useEffect(() => {
    if (!playing) return
    let raf = 0
    const tick = () => {
      draw()
      const wrap = wrapRef.current
      if (wrap) {
        const x = msToPx(currentMs())
        if (x < wrap.scrollLeft || x > wrap.scrollLeft + wrap.clientWidth - 40) {
          wrap.scrollLeft = x - wrap.clientWidth / 3
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [playing, draw, msToPx, currentMs])

  // --- interaction --------------------------------------------------------
  function localMs(e: React.PointerEvent<HTMLCanvasElement>): number {
    const rect = e.currentTarget.getBoundingClientRect()
    return pxToMs(e.clientX - rect.left)
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    // Capture keeps a drag tracking once the pointer leaves the canvas. It
    // is best-effort: a synthetic pointer id has nothing to capture, which
    // is how the end-to-end test drives this.
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // No active pointer to capture; the drag still works.
    }
    const ms = localMs(e)
    dragRef.current = { fromMs: ms, toMs: ms, mode }
    draw()
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!dragRef.current) return
    dragRef.current.toMs = localMs(e)
    draw()
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    const drag = dragRef.current
    dragRef.current = null
    if (!drag) return
    // Rounded because these are read off pixel positions, and a boundary
    // carrying fifteen decimal places of pixel arithmetic is noise: replay
    // works in whole 32ms frames regardless.
    const lo = Math.round(Math.min(drag.fromMs, drag.toMs))
    const hi = Math.round(Math.max(drag.fromMs, drag.toMs))
    if (hi - lo < CLICK_MS) {
      // Too short to be a deliberate span, so treat it as a seek.
      playheadRef.current = lo
      if (playing) void play(lo)
      draw()
      return
    }
    const span = { startMs: lo, endMs: hi }
    onChange(
      drag.mode === "paint"
        ? union(regionsRef.current, span)
        : difference(regionsRef.current, span)
    )
    try {
      e.currentTarget.releasePointerCapture(e.pointerId)
    } catch {
      // Nothing was captured; see onPointerDown.
    }
  }

  function togglePlay() {
    if (playing) {
      playheadRef.current = currentMs()
      stopPlayback()
      draw()
    } else {
      const from = playheadRef.current >= durationMs - 50 ? 0 : playheadRef.current
      void play(from)
    }
  }

  const marked = totalMs(regions)

  return (
    <div
      className="space-y-3"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === " ") {
          e.preventDefault()
          togglePlay()
        }
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={togglePlay} className="btn btn-sm btn-primary w-24">
          {playing ? "Pause" : "Play"}
        </button>

        <div className="join">
          <button
            onClick={() => setMode("paint")}
            className={`btn btn-sm join-item ${mode === "paint" ? "btn-active" : "btn-outline"}`}
          >
            Mark
          </button>
          <button
            onClick={() => setMode("erase")}
            className={`btn btn-sm join-item ${mode === "erase" ? "btn-active" : "btn-outline"}`}
          >
            Erase
          </button>
        </div>

        <div className="join">
          {zooms.map((p, i) => (
            <button
              key={p}
              onClick={() => setChosenPps(i === 0 ? null : p)}
              className={`btn btn-sm join-item ${p === pps ? "btn-active" : "btn-outline"}`}
              title={`${Math.round(p)} pixels per second`}
            >
              {i === 0 ? "fit" : `${Math.round(p / zooms[0]!)}×`}
            </button>
          ))}
        </div>

        <button
          onClick={() => onChange([])}
          disabled={regions.length === 0}
          className="btn btn-sm btn-ghost"
        >
          Clear
        </button>

        <span className="ml-auto text-sm opacity-60">
          {regions.length} region{regions.length === 1 ? "" : "s"} ·{" "}
          {(marked / 1000).toFixed(1)}s marked
        </span>
      </div>

      <div
        ref={wrapRef}
        className="overflow-x-auto rounded-lg border border-base-300 bg-black"
      >
        <canvas
          ref={canvasRef}
          style={{ width: totalPx, height: HEIGHT }}
          className={mode === "paint" ? "cursor-crosshair" : "cursor-cell"}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </div>

      <p className="text-sm opacity-60">
        Drag across the timeline to mark the stretches that should have been
        captured, including the breaths and trailing syllables you would not
        want cut off. Switch to Erase to take a stretch back, click anywhere
        to move the playhead, and press space to play.
      </p>
    </div>
  )
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds - m * 60
  const frac = s % 1 === 0 ? s.toFixed(0) : s.toFixed(1)
  return `${m}:${Number(frac) < 10 ? "0" : ""}${frac}`
}

/** Exported for the page's submit path, which stores a normalized set. */
export { normalize as normalizeRegions }
